import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useFollowUserMutation } from "Api/Mutations/Social/useFollowUserMutation";
import { useUnfollowUserMutation } from "Api/Mutations/Social/useUnfollowUserMutation";
import { useFollowersQuery } from "Api/Queries/Social/useFollowListsQuery";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { HandleGateDialog } from "Components/Social/HandleGateDialog";
import { MutualFollowConfirmDialog } from "Components/Social/MutualFollowConfirmDialog";
import { SocialSummaryRow } from "Components/Social/SocialSummaryRow";
import { SocialUserActionsMenu } from "Components/Social/SocialUserActionsMenu";
import { useHandleGate } from "Components/Social/useHandleGate";
import { useMutualFollowConfirm } from "Components/Social/useMutualFollowConfirm";
import { useCurrentUserAccessRole } from "Hooks/useCurrentUserAccessRole";
import type { FollowerDto } from "Infrastructure/Api/Api";
import { SocialListPagination } from "Pages/People/SocialListPagination";
import {
  useSnapBackToLastPage,
  useSocialListPage,
} from "Pages/People/useSocialListPage";
import { Resources, useResource } from "Translations/Resources";
import { canRoleFollow } from "Utils/Social/socialRules";

const RESOURCES = Resources.Social.People;
const PROFILE_RESOURCES = Resources.Social.PublicProfile;

type Props = {
  /**
   * Heading above the list, shown with the total. The People page carries the total in its tab
   * label instead; the merchant's followers page has no tabs, so the list names itself.
   */
  heading?: string;
};

/**
 * A page of the personas following the caller. Followers arrive through THEIR follow action, so
 * the caller may still be handle-less here — follow back and block go through the HandleGate.
 * A Customer caller can follow back from the row: instantly for a public follower (which makes
 * the pair mutual, so it is confirmed first), as a request for a private one ("Requested" until
 * they approve; clicking it withdraws). Business personas follow nobody, so their list is read-only.
 */
export const FollowersSection = ({ heading }: Props) => {
  const { t } = useResource();
  const { needsHandle, isGateOpen, openGate, closeGate, guarded } =
    useHandleGate();
  const canFollowBack = canRoleFollow(useCurrentUserAccessRole());
  const { currentPage, setPage } = useSocialListPage();
  const { data: followers, isLoading } = useFollowersQuery(currentPage);
  useSnapBackToLastPage(followers, currentPage, setPage);

  const { mutate: follow, isPending: isFollowPending } =
    useFollowUserMutation();
  const { mutate: unfollow, isPending: isUnfollowPending } =
    useUnfollowUserMutation();
  const { confirmMutual, mutualConfirmDialogProps } = useMutualFollowConfirm();

  const isMutating = isFollowPending || isUnfollowPending;

  const handleFollowBack = ({ user, requiresFollowApproval }: FollowerDto) => {
    // A private follower only gets a request — the pair becomes mutual when THEY approve, so the
    // confirm is theirs. A public one is followed instantly, which completes the pair here.
    if (requiresFollowApproval) {
      follow(user.identityUserPublicID);
      return;
    }
    confirmMutual(user, t(PROFILE_RESOURCES.FollowBack), () =>
      follow(user.identityUserPublicID),
    );
  };

  const renderFollowBackButton = (follower: FollowerDto) => {
    const { user, isFollowedByCaller, hasOutgoingRequest } = follower;

    if (!canFollowBack || isFollowedByCaller) {
      return null;
    }

    // One button either way: a pending request reads "Requested" and clicking it withdraws;
    // otherwise it follows back, through the HandleGate.
    const handleClick = hasOutgoingRequest
      ? () => unfollow(user.identityUserPublicID)
      : guarded(() => handleFollowBack(follower));
    const text = t(
      hasOutgoingRequest
        ? PROFILE_RESOURCES.Requested
        : PROFILE_RESOURCES.FollowBack,
    );

    return (
      <Button
        variant="outlined"
        color="primary"
        size="small"
        disabled={isMutating}
        onClick={handleClick}
      >
        {text}
      </Button>
    );
  };

  return (
    <PrimaryPaper>
      <Stack gap={4}>
        {heading && (
          <Typography fontWeight={600}>
            {heading}
            {!!followers?.totalCount && (
              <Typography
                component="span"
                variant="inherit"
                color="text.disabled"
                ml={1.25}
              >
                {followers.totalCount}
              </Typography>
            )}
          </Typography>
        )}
        {isLoading && (
          <Stack alignItems="center" py={1}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {followers?.totalCount === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t(RESOURCES.NoFollowers)}
          </Typography>
        )}
        {followers?.items.map((follower) => (
          <SocialSummaryRow
            key={follower.user.identityUserPublicID}
            user={follower.user}
            action={
              <>
                {renderFollowBackButton(follower)}
                <SocialUserActionsMenu
                  identityUserPublicID={follower.user.identityUserPublicID}
                  handle={follower.user.handle}
                  displayName={follower.user.displayName}
                  isBlockVisible
                  isDisabled={isMutating}
                  needsHandle={needsHandle}
                  onHandleGate={openGate}
                />
              </>
            }
          />
        ))}
        {followers && (
          <SocialListPagination
            totalCount={followers.totalCount}
            currentPage={currentPage}
            onPageChange={setPage}
          />
        )}
      </Stack>
      <HandleGateDialog isOpen={isGateOpen} onClose={closeGate} />
      <MutualFollowConfirmDialog {...mutualConfirmDialogProps} />
    </PrimaryPaper>
  );
};
